import { Matrix, Mesh, Quaternion, TransformNode, Vector3 } from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { clearArchetype, createEntityFactory, GLTFLoader, SourceParameter } from '@modules/standard';
import { World } from 'miniplex';

import { Danji2DFacilityExtendData } from '../../APIResponse';
import * as TreeModels from '../../assets/model/tree';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { TourDanjiFetcher } from '../../TourDanjiFetcher';
import { TOURDANJI_MODULE_OPTIONS_TOKEN, TourDanjiOptions } from '../../TourDanjiModule';
import { FacilityType, MeshType, TreeType } from '../component/data';
import { Entity } from '../Entity';

import { LayerMask } from '../../Constants';

// TODO: 공통 나무 모델을 사용하는 것으로 변경되었기 때문에 성능 개선의 여지가 있음
const treeModelUrls = {
  [TreeType.Normal]: {
    A: TreeModels.TreeNormalA_LOD3,
    B: TreeModels.TreeNormalB_LOD3,
    C: TreeModels.TreeNormalC_LOD3,
    D: TreeModels.TreeNormalD_LOD3,
  },
  [TreeType.Flower]: {
    A: TreeModels.TreeFlowerA_LOD3,
    B: TreeModels.TreeFlowerB_LOD3,
    C: TreeModels.TreeFlowerC_LOD3,
    D: TreeModels.TreeFlowerD_LOD3,
  },
  [TreeType.Bush]: {
    A: TreeModels.TreeBushA_LOD3,
    C: TreeModels.TreeBushC_LOD3,
  },
};

/** Material lookup map: TreeType → flavor letter → MaterialType */
const materialLookup: Record<string, Record<string, MaterialType>> = {
  [TreeType.Normal]: {
    A: MaterialType.Tree_Leave_Normal_SpringA,
    B: MaterialType.Tree_Leave_Normal_SpringB,
    C: MaterialType.Tree_Leave_Normal_SpringC,
    D: MaterialType.Tree_Leave_Normal_SpringD,
  },
  [TreeType.Flower]: {
    A: MaterialType.Tree_Leave_Flower_SpringA,
    B: MaterialType.Tree_Leave_Flower_SpringB,
    C: MaterialType.Tree_Leave_Flower_SpringC,
    D: MaterialType.Tree_Leave_Flower_SpringD,
  },
  [TreeType.Bush]: {
    A: MaterialType.Tree_Leave_Bush_Spring,
    C: MaterialType.Tree_Leave_Bush_Spring,
  },
};

/** Number of instances to create per chunk before yielding to the main thread */
const CHUNK_SIZE = 50;
const TREE_MATERIAL_TYPES: MaterialType[] = [
  MaterialType.Tree_Leave_Normal_SpringA,
  MaterialType.Tree_Leave_Normal_SpringB,
  MaterialType.Tree_Leave_Normal_SpringC,
  MaterialType.Tree_Leave_Normal_SpringD,
  MaterialType.Tree_Leave_Bush_Spring,
  MaterialType.Tree_Leave_Flower_SpringA,
  MaterialType.Tree_Leave_Flower_SpringB,
  MaterialType.Tree_Leave_Flower_SpringC,
  MaterialType.Tree_Leave_Flower_SpringD,
];

/** Yield control back to the browser to keep UI responsive */
function yieldToMain(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

interface InstanceData {
  flavorIndex: number;
  position: Vector3;
  rotationQuaternion: Quaternion;
  scale: Vector3;
}

/**
 * Tree rendering system using ThinInstance for GPU-efficient instancing.
 *
 * Each tree flavor (e.g. Normal_A, Normal_B) gets a single Mesh with a
 * ThinInstance buffer containing all transformation matrices. This avoids
 * creating individual JS objects per tree and leverages GPU instancing for
 * significantly reduced draw calls and memory usage.
 *
 * @group System
 * @category System
 */
export class TreeSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('TreeSystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly danjiOptions;

  private readonly world;
  private readonly scene;
  private readonly danjiFetcher;
  private readonly gltfLoader;
  private readonly materialManager;

  private readonly createEntity;

  private readonly treeArchetype;

  /** Base meshes loaded from GLTF assets (TreeType → Mesh[]) — used as clone source */
  private treeAssets: Map<TreeType, Mesh[]> = new Map();
  private treeOriginalScale: Map<number, Vector3> = new Map();

  /** ThinInstance-enabled flavor meshes (TreeType → Mesh[]) — each mesh holds a GPU instance buffer */
  private thinInstanceMeshes: Map<TreeType, Mesh[]> = new Map();

  //-----------------------
  constructor({
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: danjiOptions,

    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [TOURDANJI_MODULE_OPTIONS_TOKEN]: TourDanjiOptions;

    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    this.danjiOptions = danjiOptions;

    this.world = world;
    this.scene = babylonjs.scene;

    this.danjiFetcher = danjiFetcher;
    this.gltfLoader = gltfLoader;
    this.materialManager = materialManager;

    this.createEntity = createEntityFactory(this.world);

    this.treeArchetype = world.with('DanjiMeshes', 'Tree');
  }

  dispose() {
    this.thinInstanceMeshes.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.thinInstanceMeshes.clear();
    this.treeAssets.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.treeAssets.clear();
    this.treeOriginalScale.clear();
    clearArchetype(this.treeArchetype);
  }

  // --------------------------------
  public async preloadTreeAssets(parentTransform: TransformNode) {
    try {
      const treeModels = Object.keys(TreeType).flatMap((type) => {
        const value = TreeType[type as TreeType];
        return Object.entries(treeModelUrls[value]).flatMap(([flavor, src]) => {
          const assetName = `Tree_${TreeType[value]}_${flavor}_LOD${3}`;
          return {
            src,
            modelName: assetName,
            parentTransform: parentTransform,
            origibalScaling: true,
          } as SourceParameter;
        });
      });

      const result = await this.gltfLoader.loadModels(this.scene, treeModels, undefined, {
        autoLoadTexture: true,
        autoCompileShader: true,
      });

      const set = (type: TreeType, mesh: Mesh) => {
        if (!this.treeAssets.has(type)) {
          this.treeAssets.set(type, []);
        }
        this.treeAssets.get(type)!.push(mesh);
        this.treeOriginalScale.set(mesh.uniqueId, mesh.scaling.clone());
      };

      result.forEach((r) => {
        if (r.root && r.root instanceof Mesh) {
          r.root.material?.dispose();
          r.root.useVertexColors = false;

          // Determine tree type and flavor from mesh name
          const treeType = r.root.name.includes(TreeType.Normal)
            ? TreeType.Normal
            : r.root.name.includes(TreeType.Bush)
              ? TreeType.Bush
              : r.root.name.includes(TreeType.Flower)
                ? TreeType.Flower
                : undefined;

          if (treeType) {
            // Extract flavor letter from name (e.g. "_A_", "_B_", "_C_", "_D_")
            const flavorMatch = r.root.name.match(/_([A-D])_/);
            const flavor = flavorMatch?.[1] ?? 'A';
            const materialTypeKey = materialLookup[treeType]?.[flavor] ?? materialLookup[treeType]?.['A'];
            if (materialTypeKey) {
              r.root.material = this.materialManager.createMaterial(materialTypeKey);
            }
            set(treeType, r.root);
          }

          r.root.position = Vector3.Zero();
          r.root.rotation = Vector3.Zero();
          r.root.isVisible = false;
        }
      });
    } catch (ex) {
      console.warn(`Failed to fetch the tree meshes with error: ${ex}`);
    }
  }

  public async generateTrees(initiallyEnabled = true, signal?: AbortSignal) {
    if (!this.danjiFetcher.raw2dFacilityData) return;

    const treeConfigs: { treeType: TreeType; facilityName: string; meshType: MeshType }[] = [
      { treeType: TreeType.Normal, facilityName: '나무', meshType: MeshType.FACILITY_TREE_NORMAL },
      { treeType: TreeType.Bush, facilityName: '수풀', meshType: MeshType.FACILITY_TREE_BUSH },
      { treeType: TreeType.Flower, facilityName: '꽃나무', meshType: MeshType.FACILITY_TREE_FLOWER },
    ];

    for (const config of treeConfigs) {
      const meshes = this.treeAssets.get(config.treeType);
      if (!meshes || meshes.length === 0) continue;

      const dataList = this.danjiFetcher.facility2DDatas?.filter((d) => d.name === config.facilityName);
      await this.generateInstances(meshes, dataList, config.treeType, signal);

      if (signal?.aborted) return;

      const thinMeshes = this.thinInstanceMeshes.get(config.treeType) ?? [];
      await this.ensureMaterialsReady(thinMeshes, signal);
      if (signal?.aborted) return;

      // Yield to main thread to allow browser/GPU to clear any lingering state from previous abort
      await yieldToMain();
      // Aggressively clear engine caches before attempting material compilation
      this.scene.getEngine().wipeCaches(true);
      // await Promise.all(thinMeshes.map((m) => m.material?.forceCompilationAsync(m))); // Can hang on some devices
    }

    // Create ECS entities — one per TreeType (3 total instead of hundreds)
    for (const config of treeConfigs) {
      const thinMeshes = this.thinInstanceMeshes.get(config.treeType);
      if (!thinMeshes || thinMeshes.length === 0) continue;

      // Pre-entity state: set visibility before DanjiMeshSystem.initializeDanjiMeshes runs
      thinMeshes.forEach((mesh) => {
        mesh.setEnabled(initiallyEnabled);
        mesh.isVisible = true;
        mesh.isPickable = false;
        mesh.layerMask = LayerMask.Facility;
      });

      // Compute center from all flavor mesh bounding info
      let center = Vector3.Zero();
      thinMeshes.forEach((m) => center.addInPlace(m.getBoundingInfo().boundingBox.centerWorld));
      if (thinMeshes.length > 0) center.scaleInPlace(1 / thinMeshes.length);

      // NOTE: createEntity triggers DanjiMeshSystem.initializeDanjiMeshes (via onEntityAdded),
      // which applies default settings including freezeWorldMatrix() and alwaysSelectAsActiveMesh=false.
      this.createEntity<Omit<Entity, 'entityId'>>({
        Facility: {
          type: FacilityType.Tree,
          facilityName: 'Tree',
          extends: { min: Vector3.One(), max: Vector3.One() },
        },
        DanjiMeshes: {
          danjiMeshes: [{ type: config.meshType, meshes: thinMeshes }],
        },
        Tree: {
          center: center,
        },
      });

      // Post-entity: Re-apply ThinInstance-specific settings that DanjiMeshSystem may have overwritten.
      // ThinInstance requires unfrozen world matrix and alwaysSelectAsActiveMesh for correct rendering.
      thinMeshes.forEach((mesh) => {
        mesh.unfreezeWorldMatrix();
        mesh.alwaysSelectAsActiveMesh = true;
      });
    }
  }

  /** Dispose all loaded assets, generated ThinInstance meshes, and ECS entities — used for cleanup after abort or error */
  public disposeGeneratedInstances() {
    this.thinInstanceMeshes.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.thinInstanceMeshes.clear();
    this.treeAssets.forEach((ms) => ms.forEach((m) => m.dispose(false, true)));
    this.treeAssets.clear();
    this.treeOriginalScale.clear();
    // Ensure tree materials/textures are fully disposed after abort so reload starts clean
    this.materialManager.disposeMaterials(TREE_MATERIAL_TYPES);
    clearArchetype(this.treeArchetype);
  }

  private async ensureMaterialsReady(meshes: Mesh[], signal?: AbortSignal) {
    const materials = new Set(meshes.map((m) => m.material).filter(Boolean));
    const texturePromises: Promise<void>[] = [];
    materials.forEach((mat) => {
      const texList = [
        (mat as any).albedoTexture,
        (mat as any).opacityTexture,
        (mat as any).emissiveTexture,
        (mat as any).bumpTexture,
        (mat as any).metallicTexture,
        (mat as any).ambientTexture,
        (mat as any).reflectionTexture,
        (mat as any).reflectivityTexture,
        (mat as any).detailTexture,
        (mat as any).lightmapTexture,
      ].filter(Boolean);
      texList.forEach((tex: any) => {
        if (typeof tex.whenReadyAsync === 'function') {
          texturePromises.push(tex.whenReadyAsync());
        }
      });
    });
    if (texturePromises.length > 0) {
      await Promise.all(texturePromises);
    }
    if (signal?.aborted) return;
  }

  public hasTreeEntities(): boolean {
    // Query ECS for tree entities - leverages ECS architecture as single source of truth
    return this.treeArchetype.entities.length > 0;
  }

  /**
   * Unified ThinInstance generation for all tree types (Normal, Flower, Bush).
   *
   * Uses GPU instancing (ThinInstance) instead of individual InstancedMesh objects:
   * - Each flavor gets a single cloned Mesh with a matrix buffer
   * - Matrices are added in chunks of CHUNK_SIZE with yield points for UI responsiveness
   * - Buffer is finalized once after all instances are added
   *
   * This reduces JS heap usage (no per-instance objects) and draw calls
   * (one draw per flavor instead of one per instance).
   */
  private async generateInstances(
    treeMeshes: Mesh[],
    dataList: Danji2DFacilityExtendData[] | undefined,
    treeType: TreeType,
    signal?: AbortSignal,
  ) {
    if (!treeMeshes || !dataList || dataList.length === 0) {
      return;
    }

    const flavors = Object.keys(treeModelUrls[treeType]);

    // Step A: Clone base meshes for ThinInstance usage (one clone per flavor)
    const flavorMeshes: Mesh[] = treeMeshes.map((baseMesh, i) => {
      const clone = baseMesh.clone(`ThinTree_${treeType}_${flavors[i]}`);
      clone.makeGeometryUnique(); // Required: ThinInstance needs its own geometry buffer
      // Explicitly assign material to the cloned mesh
      clone.material = baseMesh.material;
      clone.parent = baseMesh.parent;
      clone.isVisible = false; // Will be made visible after thinInstanceAdd
      return clone;
    });

    // Step B: Collect per-flavor instance data
    const flavorInstances: InstanceData[][] = flavorMeshes.map(() => []);

    dataList.forEach((data) => {
      data.positions.forEach((position, i) => {
        const flavorIndex = Math.floor(Math.random() * flavors.length);
        const rotationQuaternion = data.rotations.at(i) ?? Quaternion.Identity();
        const originalScale = this.treeOriginalScale.get(treeMeshes[flavorIndex]!.uniqueId)!.clone();
        const scaleData = data.scaleInPlaces.at(i) ?? 1;
        const scale = originalScale.add(Vector3.One().scale(scaleData));

        flavorInstances[flavorIndex]!.push({ flavorIndex, position, rotationQuaternion, scale });
      });
    });

    // Step C: Add ThinInstances in chunks with yield points
    for (let fi = 0; fi < flavorMeshes.length; fi++) {
      const mesh = flavorMeshes[fi]!;
      const instances = flavorInstances[fi]!;

      if (instances.length === 0) {
        mesh.dispose(false, true); // No instances for this flavor — clean up clone
        continue;
      }

      for (let offset = 0; offset < instances.length; offset += CHUNK_SIZE) {
        const end = Math.min(offset + CHUNK_SIZE, instances.length);

        for (let j = offset; j < end; j++) {
          const inst = instances[j]!;
          const matrix = Matrix.Compose(inst.scale, inst.rotationQuaternion, inst.position);
          mesh.thinInstanceAdd(matrix, false); // false = don't update buffer yet
        }

        // Yield between chunks so the browser stays responsive
        if (end < instances.length) {
          await yieldToMain();
          if (signal?.aborted) {
            // Cleanup: dispose all cloned meshes for this tree type
            flavorMeshes.forEach((m) => m.dispose(false, true));
            return;
          }
        }
      }

      // Finalize ThinInstance buffers after all instances are added
      mesh.thinInstanceBufferUpdated('matrix');
      mesh.thinInstanceRefreshBoundingInfo();
      mesh.doNotSyncBoundingInfo = true;
      mesh.alwaysSelectAsActiveMesh = true;
    }

    // Store successfully created flavor meshes (filter out disposed/empty ones)
    this.thinInstanceMeshes.set(
      treeType,
      flavorMeshes.filter((m) => !m.isDisposed() && m.hasThinInstances),
    );
  }

  readonly operations: SystemOperationDefinition[] = [];
}
